import { ReactNode, useEffect, useState } from 'react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { cn } from '@/design-system/utils/cn';
import { ForcePasswordResetModal } from '@/features/auth/components/ForcePasswordResetModal';

interface DashboardLayoutProps {
  children: ReactNode;
  title: string;
  noContentPadding?: boolean;
}

export function DashboardLayout({ children, title, noContentPadding }: DashboardLayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem('orya-manager-sidebar-collapsed');
    if (saved) {
      setIsCollapsed(JSON.parse(saved));
    }
    const timer = setTimeout(() => setIsReady(true), 200);
    return () => clearTimeout(timer);
  }, []);

  const toggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('orya-manager-sidebar-collapsed', JSON.stringify(newState));
  };

  return (
    <div className="flex h-screen max-h-screen overflow-hidden bg-gray-50 text-gray-900">
      <Sidebar
        isOpen={isSidebarOpen}
        isCollapsed={isCollapsed}
        enableTransition={isReady}
        onClose={() => setIsSidebarOpen(false)}
        onToggleCollapse={toggleCollapse}
      />

      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col min-w-0 ease-in-out',
          isReady ? 'transition-all duration-300' : 'transition-none',
          isCollapsed ? 'lg:pl-20' : 'lg:pl-64'
        )}
      >
        <Header
          title={title}
          onMenuToggle={() => setIsSidebarOpen(true)}
          isSidebarCollapsed={isCollapsed}
        />

        <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-slate-50 pt-16 min-w-0">
          <div
            className={cn(
              'flex h-full min-h-0 min-w-0 w-full flex-col overflow-y-auto',
              !noContentPadding && 'p-4 md:p-6 lg:p-8 gap-6'
            )}
          >
            {children}
          </div>
        </main>
      </div>

      {/* KLOCKSEC: bloqueia o painel até o staff trocar uma senha fraca. */}
      <ForcePasswordResetModal />
    </div>
  );
}
